
import { addTranslations } from './VGTranslations';

/**
 * @fileOverview
 * Contains an application level helper class for displaying navigation instructions.
 * It is furnished as an example, and can be customized or used as a starting point by the developer.
 */

/**
 * @public
 * @name VGNavigation
 * @class
 * @constructor VGNavigation
 * @description
 * It allows the rendering of navigation instructions if available from computeRoute().
 * Creates a navigation object to simplify the display of instructions.
 * It uses for the media directory the value of vg.imagePath (by default '../media'), which contains:
 * <ul>
 * <li>images for transit instructions: transit_*.png
 * </ul>
 * @see vg.mapviewer.web.Mapviewer#computeRoute
 * @see vg.mapviewer.web2d.Mapviewer#computeRoute
 * @param {vg.mapviewer.Mapviewer} pMapviewer
 * @param {object} pNavigationData result of vg.mapviewer.Mapviewer.computeRoute()
 * @param {object} placesOnMap place id name correspondance
 *
 * @example
 This class asummes that the following elements exist on your .html file

<div id="instructions" class="instructions">
	<div id="instructions_prev_button" class="instructions"><img src="media/leftArrow.png"/></div>
	<div id="instructions_count" class="instructions"></div>
	<div id="instructions_brief" class="instructions"></div>
	<div id="instructions_detail" class="instructions"></div>
	<img id="instructions_icon" class="instructions"></img>
	<div id="instructions_time" class="instructions"></div>
	<div id="instructions_next_button" class="instructions"><img src="media/rightArrow.png"/></div>
</div>
 *
 * @example
pNavigationData will have the form

{ 'navigation': {
 'instructions' : [{
	'icon': 'transit_instruction_turn_left.png',
	'dataset': '0',
	'modality': 'pedestrian',
	'time': '0.000000',
	'totalTime': '45.953415','position' : { 'lat' : '48.782332', 'lon' : '2.221195' },
	'detail': 'Go straight for a few seconds then turn left',
	'brief': 'Go straight',
	'duration': ' for a few seconds'
  }
  ,...
  ]
}
* @since 1.7.18 added ID of waypoints and destination
*/
export function VGNavigation (vgContext, pNavigationData) {
  // For Debugging.
  // window.navigationData = pNavigationData;

  // false is the default for 1.7.16 and below, true for 1.7.17 and above.
  // on 1.7.17 navigation data also has parameters which where use to calculate navigation.
  var lNavigationParameters =
    pNavigationData &&
    pNavigationData.navigation &&
    pNavigationData.navigation.parameters
      ? pNavigationData.navigation.parameters
      : {};
  var mergeFloorChangeInstructions =
    lNavigationParameters &&
    typeof lNavigationParameters.mergeFloorChangeInstructions !== 'undefined'
      ? lNavigationParameters.mergeFloorChangeInstructions
      : false;

  const { mapviewer, placesOnMap } = vgContext;
  const { imagePath } = vgContext.options;

  var mValid = false;
  var instructions;
  let navigationHelper;

  // start by saying no instruction has been set
  var currentInstructionIndex = 0;
  var numberOfInstructions;

  var instructionOverlays = [];

  // If you want to highlight the curent instruction for debugging.
  var showCurrentInstructionSegment = false;

  // show all instruction segments at the beginning.
  var debugInstructionSegments = false;

  var _this = this;

  /**
   * @public
   * @name isValid
   * @function
   * @memberof VGNavigation#
   * @description
   * returns false if there was no navigation data (for example missing 'computeNavigation: true' in the routing request)
   * @return {boolean} false if there was no navigation data
   */
  this.isValid = function() {
    return mValid;
  };

  this.getNavigationHelper = function() {
    return navigationHelper;
  };

  /**
   * @public
   * @name navigationInstructionRadius
   * @type number
   * @field
   * @memberof VGNavigation
   * @description radius in meters to use when moving the camera to the beginning of an instruction.
   */
  this.navigationInstructionRadius = 50;

  if (mapviewer.sdkType === 'web2d') {
    this.navigationInstructionRadius = 25;
  }

  this.setCurrentIndex = index => {
    currentInstructionIndex = index;
    return goToCurrentInstruction();
  };
  this.currentIndex = () => currentInstructionIndex;
  this.instructionsLeft = () => numberOfInstructions - 1 - currentInstructionIndex;

  /**
   * @public
   * @name displayNextInstruction
   * @function
   * @memberof VGNavigation#
   * @description
   * displays the previous instruction if possible and move the camera to the start of the instruction
   */
  this.displayNextInstruction = function() {
    if (currentInstructionIndex < numberOfInstructions - 1) {
      currentInstructionIndex++;
      displayInstruction(currentInstructionIndex);

      return goToCurrentInstruction();
    }
    return Promise.resolve();
  };

  /**
   * @public
   * @name displayPrevInstruction
   * @function
   * @memberof VGNavigation#
   * @description
   * displays the previous instruction if possible and move the camera to the start of the instruction
   */
  this.displayPrevInstruction = function() {
    if (currentInstructionIndex > 0) {
      currentInstructionIndex--;
      displayInstruction(currentInstructionIndex);

      return goToCurrentInstruction();
    }
    return Promise.resolve();
  };

  /**
   * @public
   * @name remove
   * @function
   * @memberof VGNavigation#
   * @description
   * clear all information associated with the navigation.
   */
  this.remove = function() {
    currentInstructionIndex = 0;
    numberOfInstructions = 0;
    this.removeInstructionOverlays();
  };

  /**
   * @public
   * @name removeInstructionOverlays
   * @function
   * @memberof VGNavigation#
   * @description
   * clear any instruction extra overlays.
   */
  this.removeInstructionOverlays = function() {
    for (i in instructionOverlays) {
      instructionOverlays[i].remove();
    }
    instructionOverlays = [];
  };

  /**
   * @private
   * @description calls mapviewer.camera.goTo(), if necessary it calls mapviewer.changeFloor()
   */
  function goToCurrentInstruction() {
    if (currentInstructionIndex == -1) {
      currentInstructionIndex = 0;
    }

    var instruction = instructions[currentInstructionIndex];

    // available on SDKs and datasets with offline routing
    var position;
    var seeWholeInstruction = true;
    if (
      seeWholeInstruction &&
      instruction.positions &&
      instruction.positions.length > 0
    ) {
      var points = instruction.positions;
      var convertedPoints = [];
      for (var j = 0, jl = points.length; j < jl; j++) {
        var point = points[j];
        // transfor to new coordinates
        point = mapviewer.convertLatLonToPoint(point);

        convertedPoints.push(point);
      }
      position = mapviewer.getViewpointFromPositions({
        points: convertedPoints,
        top: 50,
        bottom: 50,
        left: 50,
        right: 50
      });

      // But do not allow to get too close.
      position.radius = Math.max(
        position.radius,
        _this.navigationInstructionRadius
      );
    } else {
      // If you want to keep same height as currently for instructions.
      // _this.navigationInstructionRadius = mapviewer.camera.position.radius;

      position = mapviewer.convertLatLonToPoint(instruction['position']);
      position.radius = _this.navigationInstructionRadius;
    }

    var currentFloor = mapviewer.getCurrentFloor();
    var instructionFloor = instruction['dataset'];

    const goTo = () => mapviewer.camera.goTo(position);

    if (currentFloor != instructionFloor) {
      return mapviewer.changeFloor(instructionFloor)
        .catch(_ => { // fail, wait prev floor change
          return new Promise(resolve => {
            console.log('floor change fail', instructionFloor);
            mapviewer.once('floorChanged', () => {
              mapviewer.changeFloor(instructionFloor)
                .then(_ => console.log('floor changed', instructionFloor))
                .then(resolve);
            });
          });
        })
        .then(goTo);
    } else {
      return goTo();
    }
  }

  /**
   * @private
   * @param {Object} instruction
   * @param {string} color
   */
  function displayInstructionSegment(instruction, color) {
    if (typeof instruction.positions !== 'undefined') {
      var overlayPoints = [];
      for (var j = 0, jl = instruction.positions.length; j < jl; j++) {
        var point = instruction.positions[j];
        // transfor to new coordinates
        point = mapviewer.convertLatLonToPoint(point);
        point.z = 2.5;
        overlayPoints.push(point);
      }
      var pathOptions = {
        floor: instruction.dataset,
        // url: trackImage, // only available on vg.mapviewer.web.Mapviewer
        // speed: lSpeed, // only available on vg.mapviewer.web.Mapviewer
        repeat: -1, // only available on vg.mapviewer.web.Mapviewer
        thickness: 2.0,
        opacity: 0.5,
        color: color, // change the color of the line
        points: overlayPoints,
        overlay: true,
        // only available on vg.mapviewer.web.Mapviewer, this makes it looks
        // better for sharp turns. Negative values will try to adapt the number of
        // segments to the length of the route, such that the absolute value
        // indicates the number of segments per "??unit??"
        segments: 1000
      };
      //
      var instructionPath = mapviewer.addRoutingPath(pathOptions);
      if (instructionPath) {
        instructionOverlays.push(instructionPath);
      }
    }
  }

  /**
   * @private
   * @description updates navigation div's with a given navigation instruction
   * @param {number} index instruction index
   * @since 1.7.10 handle .duration and .durationString, does not update currentInstructionIndex
   */
  function displayInstruction(index) {
    const instruction = instructions[index];
    /* It relies at least on the following images
			transit_instruction_end.png
			transit_instruction_down.png
			transit_instruction_up.png
			transit_instruction_start.png
			transit_instruction_straight.png
			transit_instruction_turn_gentle_left.png
			transit_instruction_turn_gentle_right.png
			transit_instruction_turn_left.png
			transit_instruction_turn_right.png
			transit_instruction_turn_sharp_left.png
			transit_instruction_turn_sharp_right.png
			transit_instruction_uturn_left.png
			transit_instruction_uturn_right.png
			transit_instruction_intermediate_destination.png
			*/

    // Configure how the line looks
    _this.removeInstructionOverlays();
    if (
      showCurrentInstructionSegment &&
      typeof instruction.positions !== 'undefined'
    ) {
      displayInstructionSegment(instruction, 0x00ff0000);
    }
  }

  var navigation = pNavigationData['navigation'];

  if (navigation !== undefined) {
    instructions = navigation['instructions'];
    if (Array.isArray(instructions)) {
      numberOfInstructions = instructions.length;
      if (numberOfInstructions > 0) {
        // Translate instructions if they come from offline routing
        // i.e. they don't have a member .brief for example.
        // If they come from the Routing Server (network), then do not translate
        if (typeof instructions[0].brief === 'undefined') {
          var translator = new VGNavigationTranslator(
            mapviewer,
            navigation,
            placesOnMap
          );
          var languageString =
            (navigation.route &&
              navigation.route.request &&
              navigation.route.request.language) ||
            'en'; // 'fr'
          translator.translateInstructions(
            instructions,
            languageString,
            mergeFloorChangeInstructions
          );
        }
        displayInstruction(0);

        if (debugInstructionSegments) {
          for (var i = 0; i < numberOfInstructions; i++) {
            displayInstructionSegment(
              instructions[i],
              i % 2 == 0 ? 0x00ff0000 : 0x0000ff00
            );
          }
        }
      }
      mValid = true;
    }
  } else {
    this.remove();
  }
}


// This code comes from VisioMove SDK: VgVGNavigationHelper.cpp
/**
 * @public
 * @name VGNavigationTranslator
 * @class
 * @description class used to translate navigation instructions coming from
 * the off-line routing engine (needs version 1.7.10 or greater).
 * Takes an instruction array and augments it with plain language descriptions.
 *
 * @param {vg.mapviewer.web.Mapviewer|vg.mapviewer.web2d.Mapviewer} mapviewer
 * @param {object} pNavigation navigate result
 * @param {object} placesOnMap map of place names.

 * @see vg.mapviewer.web.Mapviewer#computeRoute
 * @see vg.mapviewer.web2d.Mapviewer#computeRoute
 * @see VGNavigationTranslator#translateInstructions
 *
 * @example
var translator = new VGNavigationTranslator();
var languageString = 'en'; // 'fr'
translator.translateInstructions(routeResultData.navigation.instructions, languageString);
*
* @since 1.7.10
* @since 1.7.22 added mapviewer to parameter
*/
export function VGNavigationTranslator (mapviewer, navigation, placesOnMap = {}) {
  this.mapviewer = mapviewer;
  this.navigation = navigation;
  this.destinations = false;
  this.placesOnMap = placesOnMap;
  // available in 1.7.18
  if (
    navigation &&
    navigation.route &&
    navigation.route.request &&
    navigation.route.request.dst
  ) {
    this.destinations = navigation.route.request.dst;
  }
}

/**
 * @name getIDFromDestinationIndex
 * @memberof VGNavigationTranslator#
 * @function
 * @description
 * returns the  poiID of the destinationIndex of the route.
 * This is important for routes and navigations that do not traverse the given destinations in order, and to get the name of the final destination.
 *
 * @param {number} pDestinationIndex destination index
 * @return {false|string} false if it cannot be found (for example, destination is a position), or if found, the poiID at a given destination index.
 *
 */
VGNavigationTranslator.prototype.getIDFromDestinationIndex = function(pDestinationIndex) {
  if (typeof this.destinations === 'string') {
    return this.destinations;
  } else if (
    this.destinations === false ||
    !Array.isArray(this.destinations) ||
    pDestinationIndex < 0 ||
    pDestinationIndex >= this.destinations.length
  ) {
    return false;
  }
  var destinationPoiID = this.destinations[pDestinationIndex];
  if (typeof destinationPoiID === 'string') {
    return destinationPoiID;
  } else if (typeof destinationPoiID.id === 'string') {
    // this is the result of mapviewer.getRoutingNode(string);
    return destinationPoiID.id;
  }
  return false;
};

/*
* @name getPlacenameFromDestinationIndex
* @memberof VGNavigationTranslator#
* @function
* @description
* returns the  name of the destinationIndex pDestinationIndex of the route.
* This is important for routes and navigations that do not traverse the given destinations in order, and to get the name of the final destination.
*
* @param {number} destination index
* @return {false|string} false if it cannot be found, or if the the name of a destination at a given destination index.
*
* @example
var waypointName = this.getPlacenameFromDestinationIndex(lInstruction.destinationIndex);
if (waypointName)
{
	lStringNextAction += ' at '+waypointName;
}
*
*/
VGNavigationTranslator.prototype.getPlacenameFromDestinationIndex = function(pDestinationIndex) {
  var poiID = this.getIDFromDestinationIndex(pDestinationIndex);
  if (poiID !== false) {
    return this.getPlacenameFromID(poiID);
  }
  return false;
};

/**
* @name getPlacenameFromID
* @memberof VGNavigationTranslator#
* @function
* @description
* returns the  name of a placeID if known.  It looks to see if there is a POI with that ID that has a text
* if not, it tries to look in the placesOnMap object that was passed in the constructor to find that information.
*
* @param {string} pID
* @return {false|string} false if it cannot be found, or if the the name of a destination of a given poiID.
*
* @example
var nearPlaceName = navigationTranslator.getPlacenameFromID(lNearPlaceID);
if (nearPlaceName !== false)
{
...
}
*
* @since 1.7.22 uses mapviewer.getPOI first to try to get the name of the place first
*/
VGNavigationTranslator.prototype.getPlacenameFromID = function(pID, tryPOIForName = true) {
  var result = false;
  if (tryPOIForName) {
    // This won't work for Place Labels in VisioWeb2D since they don't use a POI.
    // one of the few differences between VisioWeb and VisioWeb2D
    var poi = this.mapviewer.getPOI(pID);
    if (poi && poi.length > 0) {
      poi = poi[0];
      var text = poi.options('text');
      if (typeof text === 'string' && text !== '') {
        result = text;
      }
    }
  }

  // If did not find the place on a POI (which would have been set most likely by mapviewer.setPlaceName())
  if (result === false) {
    var placesOnMap = this.placesOnMap;
    if (
      typeof placesOnMap !== 'undefined' &&
      typeof placesOnMap[pID] !== 'undefined'
    ) {
      result = placesOnMap[pID].name;
    }
  }
  return result;
};


// Format
//	en: {action}[{duration}][{nextAction}[{means}]].
//	fr: {action}[{duration}][{nextAction}[{means}]].
//
// action:
//	en: "Change transportation mode"    | "Go up"  | "Go down"   | "Go straight"
//	fr: "Changez de moyen de transport" | "Montez" | "Descendez" | "Continuez"
//
// duration:
//	en: " for a few seconds"     | " for about a minute"     | " for about {dur} minutes" | ""
//	fr: " pendant quelques secondes" | " pendant environ une minute" | " pendant environ {dur} minutes"
//
// nextAction:
//	en: " then change transportation mode"    | " go up"  | " go down"   | " go straight"
//	fr: " puis changez de moyen de transport" | " montez" | " descendez" | " continuez"
//
// means:
//	en: " using {placeName}"         | " near {placeName}"
//	fr: " en empruntant {placeName}" | " à proximité de {placeName}"

/**
 * @private
 * @name cManeuverType2Index
 * @memberof VGNavigationTranslator#
 * @description
 * conversion from maneuver string to index.
 */
VGNavigationTranslator.prototype.cManeuverType2Index = {
  eVgManeuverTypeUnknown: 0,
  eVgManeuverTypeGoStraight: 1,
  eVgManeuverTypeTurnGentleRight: 2,
  eVgManeuverTypeTurnGentleLeft: 3,
  eVgManeuverTypeTurnRight: 4,
  eVgManeuverTypeTurnLeft: 5,
  eVgManeuverTypeTurnSharpRight: 6,
  eVgManeuverTypeTurnSharpLeft: 7,
  eVgManeuverTypeUTurnRight: 8,
  eVgManeuverTypeUTurnLeft: 9,
  eVgManeuverTypeStart: 10,
  eVgManeuverTypeEnd: 11,
  eVgManeuverTypeGoUp: 12,
  eVgManeuverTypeGoDown: 13,
  eVgManeuverTypeChangeModality: 14,
  eVgManeuverTypeChangeLayer: 15,
  eVgManeuverTypeWaypoint: 16
};

/**
 * @public
 * @name translateInstructions
 * @function
 * @memberof VGNavigationTranslator#
 *
 * @param {Array} pInstructions array of instructions
 * @param {string} [pLanguageString='en'] language string like 'en' or 'fr', must be in cLanguageMap, defaults to 'en' if not found.
 * @param {boolean} [pMergeFloorChangeInstructions=false]
 * @description
 * translates all the instructions in pInstructions, augmenting each instruction with .brief, .detailed, .duration, .durationInSeconds
 * @since 1.7.17 updated signature with mergeFloorChangeInstructions
 */
VGNavigationTranslator.prototype.translateInstructions = function(
  pInstructions,
  pLanguageString,
  pMergeFloorChangeInstructions
) {
  // setup language
  var languageID =
    (pLanguageString && this.cLanguageMap[pLanguageString]) ||
    this.cLanguageMap['en'];

  pMergeFloorChangeInstructions = pMergeFloorChangeInstructions || false;

  for (var index = 0, l = pInstructions.length; index < l; index++) {
    this._translateInstruction(
      pInstructions,
      index,
      languageID,
      pMergeFloorChangeInstructions
    );
  }
};

/*
* Input:
maneuverType
dataset
modality
height
duration: in seconds
*/

/**
 * @private
 * @name _translateInstructions
 * @function
 * @memberof VGNavigationTranslator#
 *
 * @param {Array} pInstructions array of instructions
 * @param {number} pIndex index of instruction to translate
 * @param {number} [pLanguageIndex=0] language index in cLanguageMap.
 * @param {boolean} [pMergeFloorInstructions=false] if mergeFloorInstructions flag was used when computing the instructions, by default false
 * @description
 * translates all the instructions in pInstructions, augmenting each instruction with .brief, .detailed, .duration, .durationInSeconds
 */
VGNavigationTranslator.prototype._translateInstruction = function(
  pInstructions,
  pIndex,
  pLanguageIndex,
  pMergeFloorInstructions
) {
  // default to 0
  const pLang = pLanguageIndex || 0;
  // default pMergeFloorInstructions to false
  pMergeFloorInstructions = pMergeFloorInstructions || false;

  var lSkipNearPlaces = false;

  var lNumInstructions = pInstructions.length;

  var lInstruction = pInstructions[pIndex];
  var lNextInstruction = pInstructions[pIndex + 1];

  const { cManeuverType2Index } = this;
  var lInstructionManeuverIndex = cManeuverType2Index[lInstruction.maneuverType];

  var lStringAction = '';
  var lStringDuration = '';
  var lStringNextAction = '';
  var lStringVincinity = '';
  var lStringFloorChangeMethod = '';

  // enum StringType
  /** Duration link word ("for" in English, "pendant" in French) */
  var eStringFor = 0;
  /** Duration link word ("then" in English, "puis" in French) */
  var eStringThen = 1;
  /** Duration link word ("and" in English, "et" in French) */
  var eStringAnd = 2;
  /** Duration link word ("near" in English, "‡ proximitÈ de" in French) */
  var eStringNear = 3;
  /** Duration link word ("using" in English, "en empruntant" in French) */
  var eStringUsing = 4;

  var eStringStairway = 5;
  var eStringEscalator = 6;
  var eStringLift = 7;

  /** Last entry does not identify a string it is the number of strings */
  var eStringCount = 8;

  // var eVgManeuverTypeUnknown = 0;
  // var eVgManeuverTypeGoStraight = 1;
  // var eVgManeuverTypeTurnGentleRight = 2;
  // var eVgManeuverTypeTurnGentleLeft = 3;
  // var eVgManeuverTypeTurnRight = 4;
  // var eVgManeuverTypeTurnLeft = 5;
  // var eVgManeuverTypeTurnSharpRight = 6;
  // var eVgManeuverTypeTurnSharpLeft = 7;
  // var eVgManeuverTypeUTurnRight = 8;
  // var eVgManeuverTypeUTurnLeft = 9;
  // var eVgManeuverTypeStart = 10;
  // var eVgManeuverTypeEnd = 11;
  // var eVgManeuverTypeGoUp = 12;
  // var eVgManeuverTypeGoDown = 13;
  // var eVgManeuverTypeChangeModality = 14;
  // var eVgManeuverTypeChangeLayer = 15;
  // var eVgManeuverTypeWaypoint = 16;
  // var eVgManeuverTypeMax = 17;

  switch (lInstruction.maneuverType) {
  case 'eVgManeuverTypeEnd':
    lStringAction = this.cActionStringTable[pLang][lInstructionManeuverIndex];
    var lDestinationName = this.getPlacenameFromDestinationIndex(
      lInstruction.destinationIndex
    );
    if (lDestinationName) {
      lStringAction += ': ' + lDestinationName;
      lSkipNearPlaces = true;
    }
    break;
  case 'eVgManeuverTypeChangeModality':
  case 'eVgManeuverTypeChangeLayer':
  case 'eVgManeuverTypeGoDown':
  case 'eVgManeuverTypeGoUp':
  case 'eVgManeuverTypeStart':
    lStringAction = this.cActionStringTable[pLang][lInstructionManeuverIndex];
    break;
  case 'eVgManeuverTypeWaypoint':
    if (typeof lInstruction.destinationIndex !== 'undefined') {
      // This is a waypoint instructions
      lStringAction = this.cActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeWaypoint];
      var lWaypointName = this.getPlacenameFromDestinationIndex(
        lInstruction.destinationIndex
      );
      if (lWaypointName) {
        lStringAction += ': ' + lWaypointName;
        lSkipNearPlaces = true;
      }
    }
    break;
  case 'eVgManeuverTypeGoStraight':
    lStringAction = this.cActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeGoStraight];
    lStringDuration =
        this.cStringTable[pLang][eStringFor] +
        this._timeToText(lInstruction.duration / 60.0, pLang);
    if (pMergeFloorInstructions) {
      // When instruction merging is active, we have to test the next
      // instruction's layer/modality to know if we should instruct to change
      // level/transportation.
      if (!lNextInstruction) {
        // Last instruction means next action is "you have arrived"
        lStringNextAction =
            this.cStringTable[pLang][eStringThen] +
            this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeEnd];

        var lDestinationName = this.getPlacenameFromDestinationIndex(
          lInstruction.destinationIndex
        );
        if (lDestinationName) {
          lStringNextAction += ': ' + lDestinationName;
          lSkipNearPlaces = true;
        }
        break;
      }
      var lInstructionLayer = lInstruction.dataset;
      var lNextInstructionLayer =
          lNextInstruction && lNextInstruction.dataset;
      if (lInstructionLayer != lNextInstructionLayer) {
        // Test whether we go up or down or is a change of layers (possibly building)

        if (lNextInstruction.height > lInstruction.height) {
          lStringNextAction =
              this.cStringTable[pLang][eStringThen] +
              this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeGoUp];
        } else if (lNextInstruction.height < lInstruction.height) {
          lStringNextAction =
              this.cStringTable[pLang][eStringThen] +
              this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeGoDown];
        } else {
          // then is a change of layer (building)
          lStringNextAction =
              this.cStringTable[pLang][eStringThen] +
              this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeChangeLayer];
        }

        if (lNextInstruction.modality != lInstruction.modality) {
          // This works here because with mMergeFloorChangeInstructions
          // The next instruction will have the right modality
          lStringNextAction +=
              this.cStringTable[pLang][eStringAnd] +
              this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeChangeModality];
        }
      } else if (lNextInstruction.modality != lInstruction.modality) {
        lStringNextAction +=
            this.cStringTable[pLang][eStringThen] +
            this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeChangeModality];
      }

      if (
        lNextInstruction &&
          lNextInstruction.destinationIndex !== lInstruction.destinationIndex
      ) {
        // This instruction finishes at a waypoint
        lStringNextAction +=
          /* this.cStringTable[pLang][eStringAnd]*/ this.cStringTable[pLang][
            eStringThen
          ] + this.cNextActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeWaypoint];

        var lWaypointName = this.getPlacenameFromDestinationIndex(
          lInstruction.destinationIndex
        );
        if (lWaypointName) {
          lStringNextAction += ': ' + lWaypointName;
          lSkipNearPlaces = true;
        }
      }
    } else {
      // When instruction merging is inactive, we have to test the next
      // instruction's type to know if we should instruct to change level
      // or transportation mode.
      // no merge instructions.
      if (pIndex < lNumInstructions - 1) {
        {
          switch (lNextInstruction.maneuverType) {
          case 'eVgManeuverTypeChangeLayer':
            // We skip the change modality, as the modality of the
            // instruction of eVgManeuverTypeChangeModality
            // is the same as the current modality, thus the text
            // will be wrong (#6684)
            //
            // case VgNavigationModule::eVgManeuverTypeChangeModality:
          case 'eVgManeuverTypeEnd':
            lStringNextAction =
                  this.cStringTable[pLang][eStringThen] +
                  this.cNextActionStringTable[pLang][
                    this.cManeuverType2Index[lNextInstruction.maneuverType]
                  ];
            var lDestinationName = this.getPlacenameFromDestinationIndex(
              lNextInstruction.destinationIndex
            );
            if (lDestinationName) {
              lStringNextAction += ': ' + lDestinationName;
              lSkipNearPlaces = true;
            }
            break;
          case 'eVgManeuverTypeGoDown':
          case 'eVgManeuverTypeGoUp':
            lStringNextAction =
                  this.cStringTable[pLang][eStringThen] +
                  this.cNextActionStringTable[pLang][
                    this.cManeuverType2Index[lNextInstruction.maneuverType]
                  ];
            break;
          case 'eVgManeuverTypeWaypoint':
            // This is the previous instruction before the actual stop
            lStringNextAction =
                  this.cStringTable[pLang][eStringThen] +
                  this.cNextActionStringTable[pLang][
                    this.cManeuverType2Index[lNextInstruction.maneuverType]
                  ];

            var lWaypointName = this.getPlacenameFromDestinationIndex(
              lNextInstruction.destinationIndex
            );
            if (lWaypointName) {
              lStringNextAction += ': ' + lWaypointName;
              lSkipNearPlaces = true;
            }

            break;
          default:
            break;
          }
        }
      } else {
        // We are on last instruction, so no next action.
      }
    }
    break;

  default:
    // These are turn left/right instructions
    lStringAction = this.cActionStringTable[pLang][cManeuverType2Index.eVgManeuverTypeGoStraight];
    lStringDuration =
        this.cStringTable[pLang][eStringFor] +
        this._timeToText(lInstruction.duration / 60.0, pLang);
    lStringNextAction =
        this.cStringTable[pLang][eStringThen] +
        this.cNextActionStringTable[pLang][
          this.cManeuverType2Index[lInstruction.maneuverType]
        ];
    break;
  } // switch statement

  var lPlaces = lInstruction.nearPlaces;
  if (
    (pMergeFloorInstructions && pIndex >= lNumInstructions - 1) ||
    (!pMergeFloorInstructions && pIndex >= lNumInstructions - 2)
  ) {
    lSkipNearPlaces = true;
  }

  // If current instruction if a change floor kind OR
  // without mergefloor the next instruction is the change floor kind OR
  // with mergefloor, the height of the current and next instruction are different
  if (
    lInstruction.maneuverType == 'eVgManeuverTypeGoUp' ||
    lInstruction.maneuverType == 'eVgManeuverTypeGoDown' ||
    (!pMergeFloorInstructions &&
      pIndex < lNumInstructions - 2 &&
      (pInstructions[pIndex + 1].maneuverType == 'eVgManeuverTypeGoUp' ||
        pInstructions[pIndex + 1].maneuverType == 'eVgManeuverTypeGoDown')) ||
    (pMergeFloorInstructions &&
      pIndex < lNumInstructions - 1 &&
      lInstruction.height != pInstructions[pIndex + 1].height)
  ) {
    var lFound = false;
    for (var ia = 0, la = lInstruction.attributes.length; ia < la; ia++) {
      var attribute = lInstruction.attributes[ia];
      switch (attribute) {
      case 'stairway':
        lStringFloorChangeMethod =
            this.cStringTable[pLang][eStringUsing] +
            this.cStringTable[pLang][eStringStairway];
        lFound = true;
        lInstruction.icon = 'ic_stairs';
        break;
      case 'escalator':
        lStringFloorChangeMethod =
            this.cStringTable[pLang][eStringUsing] +
            this.cStringTable[pLang][eStringEscalator];
        lFound = true;
        lInstruction.icon = 'ic_escalator';
        break;
      case 'lift':
        lStringFloorChangeMethod =
            this.cStringTable[pLang][eStringUsing] +
            this.cStringTable[pLang][eStringLift];
        lFound = true;
        lInstruction.icon = 'vg_elevator';
        break;
      }
      if (lFound) {
        break;
      }
    }
  }

  if (!lSkipNearPlaces) {
    if (lPlaces && lPlaces.length > 0) {
      // places are sorted by internal angle, if you wanted them sorted by distance.
      // lPlaces.sort(function(a,b){return a.distance - b.distance;});

      // Get first place
      const place = lPlaces.find(x => this.mapviewer.getPlace(x.id)) || {};
      const lPlaceName = this.getPlacenameFromID(place.id);

      if (
        lPlaceName !== false &&
        lInstruction.maneuverType != 'eVgManeuverTypeChangeModality' &&
        lInstruction.maneuverType != 'eVgManeuverTypeChangeLayer' &&
        lInstruction.maneuverType != 'eVgManeuverTypeEnd'
      ) {
        //lStringVincinity = this.cStringTable[pLang][eStringNear] + lPlaceName;
        lStringVincinity = this.cStringTable[pLang][eStringNear].replace('%S', lPlaceName);
      }
    }
  }

  // Uncomment this line if you don't want landmark navigation.
  // lStringVincinity = '';

  // Hide duration
  lStringDuration = '';

  lInstruction.detail = lStringAction + lStringDuration + lStringNextAction;
  if (
    (lStringNextAction && lStringNextAction != '') ||
    lInstruction.maneuverType == 'eVgManeuverTypeGoUp' ||
    lInstruction.maneuverType == 'eVgManeuverTypeGoDown'
  ) {
    lInstruction.detail += lStringFloorChangeMethod + lStringVincinity;
  }
  lInstruction.brief = lStringAction;
  lInstruction.durationString = lStringDuration;

  lInstruction.detail = this._replaceTokens(
    lInstruction.detail,
    lInstruction,
    lNextInstruction
  );
  lInstruction.brief = this._replaceTokens(
    lInstruction.brief,
    lInstruction,
    lNextInstruction
  );
  lInstruction.durationString = this._replaceTokens(
    lInstruction.durationString,
    lInstruction,
    lNextInstruction
  );
}; // end NavigationSolver.prototype.translateInstruction

/**
 * @private
 * @name _replaceTokens
 * @memberof VGNavigationTranslator#
 * @function
 * @description
 * replaces tokens on a string. %d: duration in minutes, %m: current modality, %l: current dataset
 * %M modality of next instruction, %L: next dataset name.
 * @param {String} pStringWithTokens string with tokens
 * @param {InstructionObject} pInstructionCurrent
 * @param {InstructionObject} pInstructionNext
 * @return {string} with tokens replaced
 */
VGNavigationTranslator.prototype._replaceTokens = function(
  pStringWithTokens,
  pInstructionCurrent,
  pInstructionNext
) {

  const getFloorName = id => this.getPlacenameFromID(id, false) || id;

  // Replaces occurrances of a token with contextual data
  if (typeof pInstructionCurrent !== 'undefined') {
    var lDurationInMinutes = Math.floor(pInstructionCurrent.duration / 60.0);

    pStringWithTokens = pStringWithTokens.replace('%d', lDurationInMinutes);
    pStringWithTokens = pStringWithTokens.replace(
      '%m', pInstructionCurrent.modality
    );
    pStringWithTokens = pStringWithTokens.replace(
      '%l', getFloorName(pInstructionCurrent.dataset)
    );
  }

  if (typeof pInstructionNext !== 'undefined') {
    pStringWithTokens = pStringWithTokens.replace(
      '%M', pInstructionNext.modality
    );
    pStringWithTokens = pStringWithTokens.replace(
      '%L', getFloorName(pInstructionNext.dataset)
    );
  }
  return pStringWithTokens;
};

/**
 * @private
 * @name _timeToText
 * @function
 * @memberof VGNavigationTranslator#
 * @description
 * converts time in minutes to a string in a language
 * @param {number} pTimeInMinutes
 * @param {number} pLang
 * @return {string} describing duration
 */
VGNavigationTranslator.prototype._timeToText = function(pTimeInMinutes, pLang) {
  if (pTimeInMinutes < 1.0) {
    return this.cTimeStringTable[pLang][0];
  } else if (pTimeInMinutes < 2.0) {
    return this.cTimeStringTable[pLang][1];
  } else {
    return this.cTimeStringTable[pLang][2];
  }
};

addTranslations(VGNavigationTranslator);

